import { useEffect, useRef, useState } from 'react';
import { useArtifact, useKb } from '../api/hooks';
import type { Artifact } from '../api/types';
import { formatSize } from '../format';
import { sensitivity } from '../labels/labels';
import { strings } from '../strings';
import { ErrorPanel } from './ErrorPanel';
import { Icon } from './Icon';

const PREVIEW_MAX_BYTES = 25 * 1024 * 1024;
const CSV_PREVIEW_MAX_BYTES = 2 * 1024 * 1024;
const CSV_PREVIEW_ROWS = 20;

type PreviewKind = 'image' | 'pdf' | 'video' | 'csv';

export function previewKind(artifact: Pick<Artifact, 'filename' | 'detectedMimeType' | 'filetype'>): PreviewKind | undefined {
  const mime = (artifact.detectedMimeType ?? '').toLowerCase();
  const ext = (artifact.filename.split('.').pop() ?? artifact.filetype ?? '').toLowerCase();
  if (mime.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'image';
  if (mime === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (mime.startsWith('video/') || ['mp4', 'webm'].includes(ext)) return 'video';
  if (mime === 'text/csv' || ext === 'csv') return 'csv';
  return undefined;
}

/** Splits CSV text into rows; handles quoted fields and both comma and semicolon separators. */
export function parseCsv(text: string, maxRows: number): string[][] {
  const lines = text.split(/\r?\n/).filter((l) => l.length > 0).slice(0, maxRows + 1);
  const first = lines[0] ?? '';
  const sep = (first.match(/;/g)?.length ?? 0) > (first.match(/,/g)?.length ?? 0) ? ';' : ',';
  return lines.map((line) => {
    const cells: string[] = [];
    let cell = '';
    let quoted = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (quoted) {
        if (ch === '"' && line[i + 1] === '"') {
          cell += '"';
          i += 1;
        } else if (ch === '"') quoted = false;
        else cell += ch;
      } else if (ch === '"') quoted = true;
      else if (ch === sep) {
        cells.push(cell);
        cell = '';
      } else cell += ch;
    }
    cells.push(cell);
    return cells;
  });
}

function capitalise(value?: string | null) {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
}

function Preview({ tenant, artifact, kind }: { tenant: string; artifact: Artifact; kind: PreviewKind }) {
  const kb = useKb();
  const [state, setState] = useState<{ url?: string; rows?: string[][]; error?: unknown }>({});
  const limit = kind === 'csv' ? CSV_PREVIEW_MAX_BYTES : PREVIEW_MAX_BYTES;
  const tooLarge = (artifact.fileSize ?? 0) > limit;

  useEffect(() => {
    if (tooLarge) return;
    let url: string | undefined;
    let cancelled = false;
    kb.content(tenant, artifact.id, limit)
      .then(async (blob) => {
        if (cancelled) return;
        if (kind === 'csv') setState({ rows: parseCsv(await blob.text(), CSV_PREVIEW_ROWS) });
        else {
          url = URL.createObjectURL(blob);
          setState({ url });
        }
      })
      .catch((error: unknown) => !cancelled && setState({ error }));
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [kb, tenant, artifact.id, kind, limit, tooLarge]);

  if (tooLarge) return <p>{strings.item.previewTooLarge}</p>;
  if (state.error) return <ErrorPanel error={state.error} />;
  if (state.rows) {
    const [head, ...body] = state.rows;
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse text-small">
          <caption className="text-left text-neutral-muted">{strings.item.previewRows(body.length)}</caption>
          <thead>
            <tr>
              {head?.map((h, i) => (
                <th key={i} scope="col" className="border border-neutral-field bg-neutral-surface px-2 py-1 text-left">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {body.map((row, r) => (
              <tr key={r}>
                {row.map((c, i) => (
                  <td key={i} className="border border-neutral-field px-2 py-1">
                    {c}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (!state.url) return <p role="status">{strings.loading}</p>;
  if (kind === 'image') return <img src={state.url} alt={artifact.description || artifact.filename} className="max-h-[32rem] max-w-full" />;
  if (kind === 'video') return <video src={state.url} controls className="max-h-[32rem] max-w-full" />;
  return <iframe src={state.url} title={artifact.filename} className="h-[36rem] w-full border border-neutral-field" />;
}

export function FileRow({
  tenant,
  artifactId,
  role,
  itemSensitivity,
}: {
  tenant: string;
  artifactId: string;
  role: string;
  /** The item's own sensitivity level, if its metadata states one. */
  itemSensitivity?: string | null;
}) {
  const kb = useKb();
  const artifact = useArtifact(tenant, artifactId);
  const [showPreview, setShowPreview] = useState(false);
  const [downloadError, setDownloadError] = useState<unknown>();
  const dialog = useRef<HTMLDialogElement>(null);

  if (artifact.isPending) return <li className="card" role="status">{strings.loading}</li>;
  if (artifact.isError) {
    return (
      <li>
        <ErrorPanel error={artifact.error} onRetry={() => void artifact.refetch()} />
      </li>
    );
  }
  const a = artifact.data;
  // The stricter of the item's level and the file's own classification decides.
  const levels = [itemSensitivity, capitalise(a.classification)].filter(Boolean) as string[];
  const careful = levels.find((l) => sensitivity[l]?.careful);
  const kind = previewKind(a);

  const startDownload = async () => {
    setDownloadError(undefined);
    try {
      const url = await kb.downloadUrl(tenant, a.id);
      window.open(url, '_blank', 'noopener');
    } catch (error) {
      setDownloadError(error);
    }
  };
  const onDownload = () => {
    if (careful && dialog.current) dialog.current.showModal();
    else void startDownload();
  };

  return (
    <li className="card space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="m-0 text-small text-neutral-muted">{role}</p>
          <p className="m-0 break-all font-bold">{a.filename}</p>
          <p className="m-0 text-small text-neutral-muted">
            {[a.filetype?.toUpperCase(), formatSize(a.fileSize)].filter(Boolean).join(' · ')}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          {kind && (
            <button type="button" className="btn-secondary" aria-expanded={showPreview} onClick={() => setShowPreview((v) => !v)}>
              <Icon name={showPreview ? 'close' : 'eye'} />
              {showPreview ? strings.item.hidePreview : strings.item.preview}
            </button>
          )}
          <button type="button" className="btn-primary" onClick={onDownload}>
            <Icon name="download" />
            {strings.item.download}
            <span className="sr-only"> {a.filename}</span>
          </button>
        </div>
      </div>
      {downloadError !== undefined && (
        <p role="alert" className="m-0 border-l-4 border-status-error pl-3">
          {strings.download.failed}
        </p>
      )}
      {showPreview && kind && <Preview tenant={tenant} artifact={a} kind={kind} />}

      <dialog ref={dialog} aria-labelledby={`care-${a.id}`} className="max-w-lg border border-neutral-border p-6 backdrop:bg-brand-prussian/60">
        <h2 id={`care-${a.id}`} className="mt-0 flex items-center gap-2 text-h4">
          <Icon name="lock" className="h-6 w-6 text-brand-magenta" />
          {strings.download.confirmTitle}
        </h2>
        <p>{strings.download.confirmBody(sensitivity[careful ?? '']?.label ?? careful ?? '')}</p>
        <form method="dialog" className="flex flex-wrap gap-3">
          <button className="btn-primary" value="confirm" onClick={() => void startDownload()}>
            {strings.download.confirm}
          </button>
          <button className="btn-secondary" value="cancel">
            {strings.download.cancel}
          </button>
        </form>
      </dialog>
    </li>
  );
}
